import { useEffect, useState } from 'react';
import { createRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CAMINHOS,
  pastaCurta,
  ROTAS,
  type Papel,
  type Projeto,
  type VisibilidadeUsuario,
} from '@remoto/shared';
import { rotaPainel } from './painel';
import { chamar } from '../api';
import { usarSessao } from '../estado/sessao';
import {BotaoLeve, Cartao} from '../componentes/layout';
import { CaixaDeMarcar, CampoDeEscolha } from '../componentes/campos';
import { type OpcaoDoSeletor } from '../componentes/Seletor';
import { usarConfirmacao } from '../componentes/Modal';
import { Tela } from '../workspace/Tela';
import { mensagemDoErro } from '../erros';

/**
 * Os três papéis, com o que cada um pode.
 *
 * A lista solta ("leitor", "operador", "administrador") não dizia nada a quem
 * está criando a primeira conta do painel; a descrição vem da matriz de
 * permissões e cabe no popover, coisa que um `<option>` nativo não permite.
 */
const PAPEIS: OpcaoDoSeletor<Papel>[] = [
  { id: 'LEITOR', rotulo: 'leitor', descricao: 'acompanha, não comanda' },
  { id: 'OPERADOR', rotulo: 'operador', descricao: 'comanda sessões e arquivos' },
  { id: 'ADMIN', rotulo: 'administrador', descricao: 'administra máquinas e contas' },
];

interface ContaNaLista {
  id: string;
  email: string;
  nome: string;
  papel: Papel;
  ativo: boolean;
  totpAtivo: boolean;
  totpExigido: boolean;
  umAparelhoPorVez: boolean;
  verTudo: boolean;
  verMestreClaude: boolean;
  verMestreCodex: boolean;
  /** F17 (D24). Opcional: uma API anterior ao terceiro motor não o manda. */
  verMestreOpencode?: boolean;
  ultimoAcessoEm: string | null;
}

const ERROS_DE_VISIBILIDADE = {
  flag_de_mestre_exige_operador:
    'A leitura da mestre exige papel operador ou administrador — promova a conta antes.',
  projeto_desconhecido:
    'Um dos projetos marcados não existe mais. Recarregue a lista e marque de novo.',
};

const ERROS_DE_REDEFINICAO = {
  conta_desativada: 'Esta conta está desativada. Reative-a antes de mandar o link.',
  nao_encontrado: 'Esta conta não existe mais. Recarregue a lista.',
};

/** A chave do projeto como string — o mesmo separador que o servidor usa. */
const chaveDoProjeto = (p: { maquinaId: string; cwdProjeto: string }): string =>
  `${p.maquinaId}\u0000${p.cwdProjeto}`;

/**
 * O aviso do alcance transitivo (ADR-0019). Em destaque de propósito: a flag
 * da mestre não é "mais uma sessão" — é um furo deliberado no recorte, e quem
 * marca precisa ler o tamanho dele antes de salvar.
 */
function AvisoTransitivo(): React.JSX.Element {
  return (
    <p className="rounded-md border border-amber-700 bg-amber-950/40 px-3 py-2 text-xs text-amber-200">
      Atenção: quem vê a mestre lê, pelo transcript dela, conversas da frota inteira — inclusive
      sessões que a marcação individual desta conta nega.
    </p>
  );
}

/**
 * A visibilidade de uma conta: flags + PROJETOS (ADR-0020).
 *
 * ## O layout, que é metade do que esta função conserta
 *
 * Com o painel aberto, a tela quebrava: a lista crescia sem teto dentro do
 * `<li>`, a barra de estado do `Shell` (`shrink-0`, sempre presente) flutuava
 * no meio e metade da janela ficava preta. É a família de defeito que
 * `.claude/rules/web.md` descreve — bloco sem teto de altura dentro de
 * contêiner com `overflow-hidden`. No celular não dava para usar.
 *
 * O conserto tem três partes, e nenhuma é decorativa:
 *
 * 1. **A lista tem teto em `vh`**, nunca em `rem`: o que importa é quanto da
 *    tela DAQUELE aparelho ela toma. `45vh` deixa ver o contexto em volta no
 *    celular e não estoura no desktop.
 * 2. **A lista rola por si** (`overflow-y-auto`), em vez de empurrar a página.
 * 3. **O rodapé com o botão fica FORA da área que rola** — cartão com ação tem
 *    rodapé fixo. Rolar 45 projetos para poder salvar é a mesma armadilha,
 *    só que mais educada.
 *
 * A lista de projetos tem 45 linhas na frota medida; a de sessões tinha 192, e
 * é por isso que ela saiu daqui.
 */
function PainelDeVisibilidade({ conta }: { conta: ContaNaLista }): React.JSX.Element {
  const cliente = useQueryClient();
  const [erro, setErro] = useState('');
  const [rascunho, setRascunho] = useState<VisibilidadeUsuario | null>(null);

  const { data: salvo } = useQuery({
    queryKey: ['visibilidade', conta.id],
    queryFn: () => chamar<VisibilidadeUsuario>(CAMINHOS.visibilidadeDoUsuario(conta.id)),
  });
  const { data: projetos = [] } = useQuery({
    queryKey: ['projetos'],
    queryFn: () => chamar<Projeto[]>(ROTAS.projetos),
  });

  // O rascunho nasce do que está salvo e diverge só pelo que a pessoa mexeu.
  useEffect(() => {
    if (salvo) setRascunho(salvo);
  }, [salvo]);

  const salvar = useMutation({
    mutationFn: (dados: VisibilidadeUsuario) =>
      chamar<VisibilidadeUsuario>(CAMINHOS.visibilidadeDoUsuario(conta.id), {
        method: 'PUT',
        corpo: dados,
      }),
    onSuccess: () => {
      setErro('');
      void cliente.invalidateQueries({ queryKey: ['visibilidade', conta.id] });
      void cliente.invalidateQueries({ queryKey: ['usuarios'] });
    },
    onError: (e) => setErro(mensagemDoErro(e, 'Não foi possível salvar.', ERROS_DE_VISIBILIDADE)),
  });

  if (!rascunho) {
    return <p className="py-3 text-sm text-zinc-500">Carregando visibilidade…</p>;
  }

  const marcados = new Set(rascunho.projetos.map(chaveDoProjeto));
  const ehLeitor = conta.papel === 'LEITOR';
  const trocar = (mudanca: Partial<VisibilidadeUsuario>): void =>
    setRascunho({ ...rascunho, ...mudanca });
  const trocarProjeto = (projeto: Projeto, entra: boolean): void => {
    const outros = rascunho.projetos.filter((p) => chaveDoProjeto(p) !== chaveDoProjeto(projeto));
    trocar({
      projetos: entra
        ? [...outros, { maquinaId: projeto.maquinaId, cwdProjeto: projeto.cwdProjeto }]
        : outros,
    });
  };

  return (
    <div
      className="mt-3 space-y-3 rounded-md border border-zinc-800 bg-zinc-950/50 p-3"
      data-teste="painel-de-visibilidade"
    >
      {erro ? (
        <p className="rounded-md border border-red-900 bg-red-950/40 px-3 py-2 text-xs text-red-200">
          {erro}
        </p>
      ) : null}

      <CaixaDeMarcar marcada={rascunho.verTudo} aoMarcar={(verTudo) => trocar({ verTudo })}>
        <span className="text-sm text-zinc-200">Pode ver tudo</span>
        <span className="block text-xs text-zinc-500">
          Toda sessão das máquinas que o papel alcança — as mestres ficam de fora e só saem pelas
          chaves abaixo.
        </span>
      </CaixaDeMarcar>

      <div className="space-y-2">
        <CaixaDeMarcar
          marcada={rascunho.verMestreClaude}
          desabilitada={ehLeitor}
          aoMarcar={(verMestreClaude) => trocar({ verMestreClaude })}
        >
          <span className="text-sm text-zinc-200">Vê Mestre Claude</span>
          {ehLeitor ? (
            <span className="block text-xs text-zinc-500">
              indisponível para conta leitor — a leitura da mestre exige operador
            </span>
          ) : null}
        </CaixaDeMarcar>
        <CaixaDeMarcar
          marcada={rascunho.verMestreCodex}
          desabilitada={ehLeitor}
          aoMarcar={(verMestreCodex) => trocar({ verMestreCodex })}
        >
          <span className="text-sm text-zinc-200">Vê Mestre Codex</span>
          {ehLeitor ? (
            <span className="block text-xs text-zinc-500">
              indisponível para conta leitor — a leitura da mestre exige operador
            </span>
          ) : null}
        </CaixaDeMarcar>
        {/*
          A terceira (F17, D24), simétrica às duas de cima: mesma trava de
          LEITOR, mesmo aviso. Ausente na resposta é "a API ainda não conhece a
          flag", e a caixa nasce desmarcada SEM mandar nada — o campo é opcional
          no PUT justamente para uma tela não apagar o que outro admin ligou.
        */}
        <CaixaDeMarcar
          marcada={rascunho.verMestreOpencode ?? false}
          desabilitada={ehLeitor}
          aoMarcar={(verMestreOpencode) => trocar({ verMestreOpencode })}
        >
          <span className="text-sm text-zinc-200">Vê Mestre OpenCode</span>
          {ehLeitor ? (
            <span className="block text-xs text-zinc-500">
              indisponível para conta leitor — a leitura da mestre exige operador
            </span>
          ) : null}
        </CaixaDeMarcar>
        {rascunho.verMestreClaude || rascunho.verMestreCodex || rascunho.verMestreOpencode ? (
          <AvisoTransitivo />
        ) : null}
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
          Projetos liberados ({marcados.size})
        </p>
        <p className="text-xs text-zinc-500">
          Um projeto é uma pasta numa máquina. Conversa nova criada nessa pasta{' '}
          <strong className="font-medium text-zinc-300">nasce visível</strong> para quem tem o
          projeto — só perde acesso se um administrador desmarcar.
        </p>
        {/*
          Teto em `vh` e rolagem própria: sem isso a lista empurrava a página,
          a barra de estado flutuava e metade da tela ficava vazia. Ver o
          bloco de cima para as três partes do conserto.
        */}
        <div
          className="max-h-[45vh] space-y-1.5 overflow-y-auto rounded-md border border-zinc-800/70 bg-zinc-950/40 p-2"
          data-teste="lista-de-projetos"
        >
          {projetos.length === 0 ? (
            <p className="px-1 py-2 text-xs text-zinc-500">Nenhum projeto na frota agora.</p>
          ) : (
            projetos.map((projeto) => (
              <CaixaDeMarcar
                key={chaveDoProjeto(projeto)}
                marcada={marcados.has(chaveDoProjeto(projeto))}
                aoMarcar={(entra) => trocarProjeto(projeto, entra)}
              >
                <span className="block truncate text-sm text-zinc-300">
                  {projeto.maquinaNome} · {pastaCurta(projeto.cwdExibicao)}
                </span>
                {/*
                  O caminho CRU, e não a chave normalizada: `c:/users/...` em
                  minúsculas é o que o banco casa, não o que a pessoa
                  reconhece como a pasta dela.
                */}
                <span className="block truncate font-mono text-[11px] text-zinc-600">
                  {projeto.cwdExibicao} · {projeto.sessoes} conversa(s)
                </span>
              </CaixaDeMarcar>
            ))
          )}
        </div>
      </div>

      {/* Rodapé FORA da área que rola: o botão que decide não se persegue. */}
      <div className="flex justify-end border-t border-zinc-800/70 pt-3">
        <button
          type="button"
          disabled={salvar.isPending}
          onClick={() => salvar.mutate(rascunho)}
          className="rounded-md bg-zinc-100 px-4 py-1.5 text-sm font-medium text-zinc-900 transition hover:bg-white disabled:opacity-50"
        >
          Salvar visibilidade
        </button>
      </div>
    </div>
  );
}

function Usuarios(): React.JSX.Element {
  const cliente = useQueryClient();
  const eu = usarSessao((e) => e.usuario);
  const [erro, setErro] = useState('');
  /** Confirmação visível de um gesto que não muda nada na tela (o envio). */
  const [recado, setRecado] = useState('');
  const [expandida, setExpandida] = useState<string | null>(null);
  const [novo, setNovo] = useState({ nome: '', email: '', senha: '', papel: 'LEITOR' as Papel });

  const { data: contas = [], isLoading } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => chamar<ContaNaLista[]>(ROTAS.usuarios),
  });

  const recarregar = (): void => {
    setErro('');
    void cliente.invalidateQueries({ queryKey: ['usuarios'] });
  };

  const criar = useMutation({
    mutationFn: () => chamar<ContaNaLista>(ROTAS.usuarios, { method: 'POST', corpo: novo }),
    onSuccess: () => {
      setNovo({ nome: '', email: '', senha: '', papel: 'LEITOR' });
      recarregar();
    },
    onError: (e) => setErro(mensagemDoErro(e, 'Não foi possível concluir.', { validacao: 'Confira os campos: a senha precisa de pelo menos 12 caracteres.' })),
  });

  const alterar = useMutation({
    mutationFn: (mudanca: { id: string; dados: Partial<ContaNaLista> }) =>
      chamar<ContaNaLista>(`${ROTAS.usuarios}/${mudanca.id}`, {
        method: 'PATCH',
        corpo: mudanca.dados,
      }),
    onSuccess: recarregar,
    onError: (e) => setErro(mensagemDoErro(e, 'Não foi possível concluir.', { validacao: 'Confira os campos: a senha precisa de pelo menos 12 caracteres.' })),
  });

  const remover = useMutation({
    mutationFn: (id: string) => chamar<void>(`${ROTAS.usuarios}/${id}`, { method: 'DELETE' }),
    onSuccess: recarregar,
    onError: (e) => setErro(mensagemDoErro(e, 'Não foi possível concluir.', { validacao: 'Confira os campos: a senha precisa de pelo menos 12 caracteres.' })),
  });

  /*
    A rota é ADMIN e AGUARDA o SMTP de propósito (ADR-0020): a pública
    `/auth/senha/esquecer` é anti-enumeração e responde igual exista ou não a
    conta, sem esperar o envio. Reusá-la daria ao admin uma tela que nunca
    falha e nunca confirma nada — o mesmo silêncio que custou horas no dia em
    que a VPS bloqueava a porta 465. Por isso a resposta traz `enviou`, e por
    isso `enviou: false` vira AVISO na tela, não sucesso.
  */
  const redefinir = useMutation({
    mutationFn: (conta: ContaNaLista) =>
      chamar<{ enviou: boolean; email: string }>(CAMINHOS.redefinicaoDoUsuario(conta.id), {
        method: 'POST',
      }),
    onSuccess: (r) => {
      setErro('');
      setRecado(
        r.enviou
          ? `Link de redefinição enviado para ${r.email}. Ele vale por 30 minutos e serve uma vez só.`
          : `O servidor de e-mail RECUSOU o envio para ${r.email}. O link foi criado, mas não saiu: confira a configuração de SMTP antes de avisar a pessoa.`,
      );
    },
    onError: (e) => {
      setRecado('');
      setErro(mensagemDoErro(e, 'Não foi possível enviar o link.', ERROS_DE_REDEFINICAO));
    },
  });

  const [confirmar, dialogo] = usarConfirmacao();

  return (
    <Tela>
      {dialogo}
      {erro ? (
        <p className="rounded-md border border-red-900 bg-red-950/40 px-4 py-2 text-sm text-red-200">
          {erro}
        </p>
      ) : null}
      {recado ? (
        <p
          data-teste="recado-da-redefinicao"
          className="rounded-md border border-emerald-800 bg-emerald-950/40 px-4 py-2 text-sm text-emerald-200"
        >
          {recado}
        </p>
      ) : null}

      <Cartao titulo="Nova conta">
        <form
          className="grid gap-3 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            criar.mutate();
          }}
        >
          <input
            value={novo.nome}
            onChange={(e) => setNovo({ ...novo, nome: e.target.value })}
            placeholder="Nome"
            className="rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none focus:border-zinc-500"
          />
          <input
            value={novo.email}
            onChange={(e) => setNovo({ ...novo, email: e.target.value })}
            placeholder="e-mail"
            type="email"
            autoComplete="off"
            className="rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none focus:border-zinc-500"
          />
          <input
            value={novo.senha}
            onChange={(e) => setNovo({ ...novo, senha: e.target.value })}
            placeholder="senha provisória (12+ caracteres)"
            type="password"
            autoComplete="new-password"
            className="rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none focus:border-zinc-500"
          />
          <div className="flex gap-3">
            <CampoDeEscolha<Papel>
              rotulo="Papel da nova conta"
              rotuloOculto
              className="flex-1"
              valor={novo.papel}
              opcoes={PAPEIS}
              aoEscolher={(papel) => setNovo({ ...novo, papel })}
            />
            <button
              type="submit"
              disabled={criar.isPending}
              className="rounded-md bg-zinc-100 px-4 py-2 font-medium text-zinc-900 transition hover:bg-white disabled:opacity-50"
            >
              Criar
            </button>
          </div>
        </form>
      </Cartao>

      <Cartao titulo="Contas">
        {isLoading ? (
          <p className="text-sm text-zinc-500">Carregando…</p>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {contas.map((c) => (
              <li key={c.id} className="py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-zinc-100">{c.nome}</span>
                      {!c.ativo ? <span className="text-xs text-amber-300">desativada</span> : null}
                      {c.id === eu?.id ? <span className="text-xs text-zinc-500">você</span> : null}
                    </div>
                    <p className="mt-0.5 text-xs text-zinc-500">
                      {c.email} · segundo fator {c.totpAtivo ? 'ativo' : 'pendente'}
                    </p>
                    {/*
                      ADMIN exige sempre, por política — a caixa aparece marcada
                      e desabilitada para dizer isso, não para ser clicada. A
                      exigência de não-ADMIN vale no PRÓXIMO login da pessoa.
                    */}
                    <CaixaDeMarcar
                      marcada={c.papel === 'ADMIN' ? true : c.totpExigido}
                      desabilitada={c.papel === 'ADMIN'}
                      aoMarcar={(marcada) =>
                        alterar.mutate({ id: c.id, dados: { totpExigido: marcada } })
                      }
                      className="mt-1.5"
                    >
                      <span className="text-xs text-zinc-400">
                        Exigir segundo fator
                        {c.papel === 'ADMIN' ? ' (sempre exigido para administrador)' : ''}
                      </span>
                    </CaixaDeMarcar>
                    {/*
                      Um aparelho por vez (ADR-0020). NASCE DESLIGADA: entrar
                      em vários navegadores com a mesma conta passou a ser o
                      padrão. Ligar vale no PRÓXIMO login — ligar uma
                      preferência não é o mesmo que expulsar quem já entrou.
                    */}
                    <CaixaDeMarcar
                      marcada={c.umAparelhoPorVez}
                      aoMarcar={(marcada) =>
                        alterar.mutate({ id: c.id, dados: { umAparelhoPorVez: marcada } })
                      }
                      className="mt-1.5"
                    >
                      <span className="text-xs text-zinc-400">
                        Um aparelho por vez
                        <span className="block text-[11px] text-zinc-600">
                          entrar derruba a sessão anterior desta conta
                        </span>
                      </span>
                    </CaixaDeMarcar>
                  </div>
                  {/*
                    `flex-wrap` e `min-w-0`: a fila de ações tem cinco peças
                    (papel, Visibilidade, senha, Desativar, Remover) e não cabe
                    em 390 px. Sem quebrar linha ela ATRAVESSA a borda direita
                    — e como a moldura é `overflow-hidden`, o que passa é
                    cortado sem barra de rolagem nenhuma avisando: os botões do
                    fim simplesmente não existem para quem está no telefone.
                    O pai já era `flex-wrap`; esta fila de dentro não era, e
                    era ela que estourava. Medido em `contas-layout.spec.ts`.
                  */}
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <CampoDeEscolha<Papel>
                      // O nome entra no rótulo porque a tela tem um destes por
                      // linha: "Papel" sozinho, repetido dez vezes, não diz de quem.
                      rotulo={'Papel de ' + c.nome}
                      rotuloOculto
                      compacto
                      valor={c.papel}
                      opcoes={PAPEIS}
                      aoEscolher={(papel) => alterar.mutate({ id: c.id, dados: { papel } })}
                    />
                    <BotaoLeve onClick={() => setExpandida(expandida === c.id ? null : c.id)}>
                      {expandida === c.id ? 'Fechar visibilidade' : 'Visibilidade'}
                    </BotaoLeve>
                    {/*
                      Dispara o link de redefinição para a caixa da pessoa
                      (ADR-0020). O admin dispara, não lê: o link vai para o
                      e-mail dela e nunca aparece aqui. Até aqui o único gesto
                      de senha depois da criação era o admin DIGITAR uma nova e
                      transmiti-la por fora — que é o que a recuperação por
                      e-mail veio resolver.
                    */}
                    <BotaoLeve
                      onClick={() =>
                        confirmar({
                          titulo: 'Enviar link de redefinição',
                          texto: `Um link de uso único vai para ${c.email}, válido por 30 minutos. A senha atual continua valendo até a pessoa usar o link, e a verificação em duas etapas não muda.`,
                          rotuloConfirmar: 'Enviar',
                          aoConfirmar: () => redefinir.mutate(c),
                        })
                      }
                    >
                      Enviar link de senha
                    </BotaoLeve>
                    <BotaoLeve onClick={() => alterar.mutate({ id: c.id, dados: { ativo: !c.ativo } })}>
                      {c.ativo ? 'Desativar' : 'Reativar'}
                    </BotaoLeve>
                    <BotaoLeve
                      onClick={() =>
                        confirmar({
                          titulo: 'Remover conta',
                          texto: `A conta de ${c.nome} (${c.email}) deixa de existir. O que ela já fez continua na trilha de auditoria.`,
                          rotuloConfirmar: 'Remover',
                          perigosa: true,
                          aoConfirmar: () => remover.mutate(c.id),
                        })
                      }
                    >
                      Remover
                    </BotaoLeve>
                  </div>
                </div>
                {expandida === c.id ? <PainelDeVisibilidade conta={c} /> : null}
              </li>
            ))}
          </ul>
        )}
      </Cartao>
    </Tela>
  );
}

export const rotaUsuarios = createRoute({
  getParentRoute: () => rotaPainel,
  path: '/usuarios',
  beforeLoad: async () => {
    // A API barra de novo; aqui é só para não abrir uma tela que vai falhar.
    if (usarSessao.getState().usuario?.papel !== 'ADMIN') throw redirect({ to: '/maquinas' });
  },
  component: Usuarios,
});
